/**
 * Runtime helpers: department-assigned RA template → create/edit bill.
 * Does not replace CPWD; only activates when CONFIGURABLE snapshot is present.
 *
 * Freeze policy:
 * - Each bill stores its own template snapshot + field values at create/edit.
 * - Changing the department template only affects NEW bills.
 * - Existing bills keep frozen template/amounts until the user edits them in UI.
 */

export const cloneJson = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_e) {
    return value;
  }
};

export const isConfigurableSnapshot = (snapOrBill) => {
  const engine = String(
    snapOrBill?.calculation_engine
      || snapOrBill?.calculationEngine
      || snapOrBill?.ra_template_snapshot?.calculationEngine
      || '',
  ).toUpperCase();
  const snap = snapOrBill?.ra_template_snapshot || snapOrBill?.snapshot || snapOrBill;
  return engine === 'CONFIGURABLE' && Array.isArray(snap?.fields) && snap.fields.length > 0;
};

/** True when this saved bill should keep frozen amounts (not live-overwritten by register). */
export const hasFrozenTemplateCalculation = (bill = {}) => (
  isConfigurableSnapshot(bill)
  && Boolean(bill.calculation_snapshot || bill.template_field_values)
);

export const getTemplateSnapshot = (billOrResolve) => (
  billOrResolve?.ra_template_snapshot
  || billOrResolve?.ra_template?.snapshot
  || null
);

/**
 * Deep-cloned template bundle persisted on every create / amount edit.
 * Never point at live department template objects.
 */
export const buildTemplateFreezePayload = (bill = {}, overrides = {}) => {
  const snap = overrides.ra_template_snapshot !== undefined
    ? overrides.ra_template_snapshot
    : bill.ra_template_snapshot;
  const calcSnap = overrides.calculation_snapshot !== undefined
    ? overrides.calculation_snapshot
    : bill.calculation_snapshot;
  const engine = overrides.calculation_engine !== undefined
    ? overrides.calculation_engine
    : (isConfigurableSnapshot({ ...bill, ra_template_snapshot: snap, calculation_engine: bill.calculation_engine })
      ? 'CONFIGURABLE'
      : (bill.calculation_engine ?? null));

  return {
    ra_template_id: overrides.ra_template_id !== undefined ? overrides.ra_template_id : (bill.ra_template_id ?? null),
    ra_template_code: overrides.ra_template_code !== undefined ? overrides.ra_template_code : (bill.ra_template_code ?? null),
    ra_template_version: overrides.ra_template_version !== undefined ? overrides.ra_template_version : (bill.ra_template_version ?? null),
    ra_template_snapshot: cloneJson(snap ?? null),
    calculation_engine: engine,
    template_field_inputs: cloneJson(
      overrides.template_field_inputs !== undefined
        ? overrides.template_field_inputs
        : (bill.template_field_inputs || {}),
    ),
    template_field_values: cloneJson(
      overrides.template_field_values !== undefined
        ? overrides.template_field_values
        : (bill.template_field_values || {}),
    ),
    previous_template_values: cloneJson(
      overrides.previous_template_values !== undefined
        ? overrides.previous_template_values
        : (bill.previous_template_values || {}),
    ),
    calculation_snapshot: cloneJson(calcSnap ?? null),
  };
};

/** Apply resolve API template payload onto a new/draft bill (create only — never on existing bills). */
export const applyResolvedTemplateToBill = (bill = {}, resolvePayload = {}) => {
  const tpl = resolvePayload?.ra_template;
  if (!tpl?.snapshot || !isConfigurableSnapshot(tpl.snapshot)) {
    return {
      ...bill,
      ra_template_id: null,
      ra_template_code: null,
      ra_template_version: null,
      ra_template_snapshot: null,
      calculation_engine: null,
      previous_template_values: {},
    };
  }
  return {
    ...bill,
    ra_template_id: tpl.id,
    ra_template_code: tpl.code,
    ra_template_version: tpl.version,
    // Clone so later department/template edits cannot mutate this draft by reference
    ra_template_snapshot: cloneJson(tpl.snapshot),
    calculation_engine: 'CONFIGURABLE',
    template_field_inputs: bill.template_field_inputs && typeof bill.template_field_inputs === 'object'
      ? { ...bill.template_field_inputs }
      : {},
  };
};

/** Reverse of router SEMANTIC_TO_LEGACY — bridge CPWD previous bills into template field codes. */
const LEGACY_BY_SEMANTIC = {
  GROSS_UPTO_DATE: ['upto_date_gross_value_of_work_done', 'upto_date_gross_inc_sec_adv', 'gross_work_done'],
  CURRENT_RA_VALUE: ['current_bill_value', 'net_value_work_done_since_prev_bill'],
  TOTAL_RECOVERIES: ['total_recoveries'],
  TOTAL_WITHHELD: ['total_withheld'],
  NET_PAYABLE: ['net_payment_received', 'net_payable'],
  PROGRESS_PERCENTAGE: ['percent_workdone'],
  SECURITY_DEPOSIT: ['security_deposit'],
  GST_DEDUCTION: ['gst_deduction'],
  INCOME_TAX: ['income_tax_deduction'],
  LABOUR_CESS: ['labour_cess'],
  PREVIOUS_RA_GROSS: ['gross_value_as_per_previous_ra_bill', 'previous_ra_gross_inc_sec_adv'],
  SECURED_ADVANCE_ADDED: ['secured_advance_added_in_this_bill'],
};

const pickLegacy = (bill, keys) => {
  for (const k of keys) {
    if (bill[k] != null && bill[k] !== '') return Number(bill[k]) || 0;
  }
  return null;
};

/**
 * Previous RA field map for PREVIOUS_RA_FIELD / Previous RA behaviours.
 * Prefers frozen calculation_snapshot from the previous bill.
 * @param {object|null} previousBill
 * @param {object|null} currentSnapshot — current bill template (for semantic role bridging)
 */
export const buildPreviousTemplateValues = (previousBill, currentSnapshot = null) => {
  if (!previousBill) return {};
  const fromSnapshot = previousBill.calculation_snapshot?.fieldValues
    || previousBill.calculation_snapshot?.effectiveFieldValues
    || previousBill.template_field_values
    || null;
  if (fromSnapshot && typeof fromSnapshot === 'object' && Object.keys(fromSnapshot).length > 0) {
    return { ...fromSnapshot };
  }
  const out = {};
  const snap = previousBill.ra_template_snapshot || currentSnapshot;
  if (snap && Array.isArray(snap.fields)) {
    snap.fields.forEach((f) => {
      const code = f.fieldCode;
      if (!code) return;
      if (previousBill[code] != null && previousBill[code] !== '') {
        out[code] = Number(previousBill[code]) || previousBill[code];
        return;
      }
      if (previousBill.template_field_inputs?.[code] != null) {
        out[code] = Number(previousBill.template_field_inputs[code]) || previousBill.template_field_inputs[code];
        return;
      }
      const role = f.semanticRole ? String(f.semanticRole) : '';
      const legacyKeys = LEGACY_BY_SEMANTIC[role];
      if (legacyKeys) {
        const v = pickLegacy(previousBill, legacyKeys);
        if (v != null) out[code] = v;
      }
    });
  }
  return out;
};

/**
 * Manual INPUT values for the current bill's template evaluation.
 */
export const buildTemplateInputs = (bill = {}, snapshot = null) => {
  const snap = snapshot || bill.ra_template_snapshot;
  const inputs = { ...(bill.template_field_inputs || {}) };
  const fields = Array.isArray(snap?.fields) ? snap.fields : [];

  fields.forEach((f) => {
    const code = f.fieldCode;
    if (!code) return;
    const type = String(f.fieldType || 'INPUT').toUpperCase();
    if (type !== 'INPUT' && type !== 'LOOKUP') return;
    if (inputs[code] != null && inputs[code] !== '') return;
    if (bill[code] != null && bill[code] !== '') {
      inputs[code] = bill[code];
    }
  });

  // Bridge common CPWD create-form keys into standard template field codes when present.
  const bridgesByCode = {
    GROSS_WORK_DONE: bill.upto_date_gross_work_done ?? bill.gross_value_of_work_done,
    SECURED_ADVANCE: bill.sec_advance_material,
    MOBILISATION_ADVANCE: bill.mobilisation_advance,
    CLAUSE_10CC_10CA: bill.clause_10cc_10ca_payment,
    ADVANCE_NOT_MEASURED: bill.advance_payment_workdone_not_measured,
    ADDL_SD_SPECIAL: bill.additional_sd_special_items,
    TOTAL_WITHHELD: bill.total_withheld,
    AGREEMENT_AMOUNT: bill.agreement_amount,
  };
  Object.entries(bridgesByCode).forEach(([code, value]) => {
    if (inputs[code] == null && value != null && value !== '') {
      inputs[code] = value;
    }
  });

  // Also bridge by semantic role onto INPUT fields (e.g. GROSS_AMOUNT ← work done).
  const semanticBridge = {
    GROSS_UPTO_DATE: bill.upto_date_gross_work_done ?? bill.gross_value_of_work_done,
    CURRENT_RA_VALUE: bill.current_bill_value,
    TOTAL_WITHHELD: bill.total_withheld,
    AGREEMENT_AMOUNT: bill.agreement_amount,
    SECURED_ADVANCE_ADDED: bill.sec_advance_material,
  };
  fields.forEach((f) => {
    const code = f.fieldCode;
    if (!code || (inputs[code] != null && inputs[code] !== '')) return;
    const type = String(f.fieldType || 'INPUT').toUpperCase();
    if (type !== 'INPUT' && type !== 'LOOKUP') return;
    const role = f.semanticRole ? String(f.semanticRole) : '';
    const bridged = semanticBridge[role];
    if (bridged != null && bridged !== '') inputs[code] = bridged;
  });

  return inputs;
};

export const listEditableTemplateFields = (snapshot) => {
  if (!snapshot || !Array.isArray(snapshot.fields)) return [];
  return snapshot.fields
    .filter((f) => {
      const st = String(f.status || 'ACTIVE').toUpperCase();
      if (st === 'INACTIVE' || st === 'DEPRECATED') return false;
      const type = String(f.fieldType || 'INPUT').toUpperCase();
      return type === 'INPUT' || type === 'LOOKUP';
    })
    .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
};

export const listVisibleTemplateFields = (snapshot) => {
  if (!snapshot || !Array.isArray(snapshot.fields)) return [];
  return snapshot.fields
    .filter((f) => {
      const st = String(f.status || 'ACTIVE').toUpperCase();
      if (st === 'INACTIVE' || st === 'DEPRECATED') return false;
      return f.visible !== false;
    })
    .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
};

/** Group visible template fields under their sections for bill form rendering. */
export const groupTemplateFieldsBySection = (snapshot) => {
  if (!snapshot) return [];
  const fields = listVisibleTemplateFields(snapshot);
  const sections = Array.isArray(snapshot.sections)
    ? [...snapshot.sections].sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0))
    : [];
  const bySectionId = new Map();
  const bySectionCode = new Map();
  sections.forEach((s) => {
    if (s.id != null) bySectionId.set(Number(s.id), s);
    if (s.sectionCode) bySectionCode.set(String(s.sectionCode), s);
  });

  const buckets = new Map();
  const ensureBucket = (key, meta) => {
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        sectionCode: meta?.sectionCode || key,
        sectionName: meta?.sectionName || meta?.sectionCode || 'Bill Fields',
        sequence: Number(meta?.sequence || 9999),
        fields: [],
      });
    }
    return buckets.get(key);
  };

  fields.forEach((f) => {
    let section = null;
    if (f.sectionId != null) section = bySectionId.get(Number(f.sectionId));
    if (!section && f.sectionCode) section = bySectionCode.get(String(f.sectionCode));
    const key = section
      ? `id:${section.id || section.sectionCode}`
      : (f.sectionCode ? `code:${f.sectionCode}` : 'other');
    const bucket = ensureBucket(key, section || { sectionCode: f.sectionCode || 'OTHER', sectionName: 'Other Fields', sequence: 9999 });
    bucket.fields.push(f);
  });

  return Array.from(buckets.values())
    .map((b) => ({
      ...b,
      fields: b.fields.sort((a, c) => Number(a.sequence || 0) - Number(c.sequence || 0)),
    }))
    .sort((a, b) => a.sequence - b.sequence);
};

export const isTemplateInputField = (field) => {
  const type = String(field?.fieldType || 'INPUT').toUpperCase();
  return type === 'INPUT' || type === 'LOOKUP';
};

/** Current display value for a template field on a saved/draft bill. */
export const getTemplateFieldValueFromBill = (bill = {}, field) => {
  if (!field?.fieldCode) return 0;
  const code = field.fieldCode;
  const values = bill.template_field_values
    || bill.calculation_snapshot?.effectiveFieldValues
    || bill.calculation_snapshot?.fieldValues
    || {};
  if (values[code] != null && values[code] !== '') return values[code];
  if (bill.template_field_inputs?.[code] != null && bill.template_field_inputs[code] !== '') {
    return bill.template_field_inputs[code];
  }
  if (bill[code] != null && bill[code] !== '') return bill[code];
  return field.defaultValue ?? 0;
};

/**
 * Flat rows for print/view: [{ sectionName, fieldLabel, fieldCode, fieldType, current, previous, since }]
 */
export const buildTemplateComparisonRows = (bill = {}, previousBill = null) => {
  const snap = bill.ra_template_snapshot;
  if (!snap || !isConfigurableSnapshot(bill)) return [];
  const prevMap = buildPreviousTemplateValues(previousBill, snap);
  const sections = groupTemplateFieldsBySection(snap);
  const rows = [];
  sections.forEach((section) => {
    section.fields.forEach((field, idx) => {
      const current = Number(getTemplateFieldValueFromBill(bill, field)) || 0;
      const previous = Number(prevMap[field.fieldCode] ?? 0) || 0;
      rows.push({
        sectionName: idx === 0 ? section.sectionName : '',
        sectionKey: section.key,
        fieldLabel: field.fieldLabel || field.fieldCode,
        fieldCode: field.fieldCode,
        fieldType: String(field.fieldType || 'INPUT').toUpperCase(),
        current,
        previous,
        since: current - previous,
      });
    });
  });
  return rows;
};

/** Department FY recovery rates for DEPARTMENT_FIELD AST nodes (with safe defaults). */
export const buildDepartmentValuesFromRule = (ruleOrInfo = null) => {
  const rule = ruleOrInfo?.rule && typeof ruleOrInfo.rule === 'object'
    ? ruleOrInfo.rule
    : (ruleOrInfo && typeof ruleOrInfo === 'object' ? ruleOrInfo : null);
  const defaults = {
    security_deposit_pct: 2.5,
    gst_pct: 2,
    income_tax_pct: 2,
    labour_cess_pct: 1,
  };
  if (!rule) {
    return { ...defaults, _fy_rule_found: 0 };
  }
  return {
    security_deposit_pct: Number(rule.security_deposit_pct ?? defaults.security_deposit_pct),
    gst_pct: Number(rule.gst_pct ?? defaults.gst_pct),
    income_tax_pct: Number(rule.income_tax_pct ?? defaults.income_tax_pct),
    labour_cess_pct: Number(rule.labour_cess_pct ?? defaults.labour_cess_pct),
    _fy_rule_found: 1,
  };
};
